import { Link } from "react-router-dom";

/*
 * Blog article: "Getting the Most Out of Your Supplements"
 * Rendered inside BlogPost.jsx via <article className="legal-prose">.
 */
export default function ArticleBody() {
    return (
        <>
            <h2>Quick answer</h2>
            <p>
                Two things decide whether a supplement does anything for
                you. Whether it is the right one for the job, and whether
                you gave it long enough to work.
            </p>
            <p>
                A supplement can be doing exactly what it should while
                you give up on it after a week, because you're just not
                feeling it. Often the cause is simply accumulation. It
                has not had time to build up to the level where it does
                anything.
            </p>
            <p>
                TRT Plus charts this according to unique pharmacokinetic
                milestones, so you can see when a supplement has actually
                reached its potential.
            </p>

            <h2>Is it the right one for the job</h2>
            <p>
                Most supplements are worth taking for one of two reasons.
                Either you are correcting a deficiency, or you are
                targeting an intake that food alone will not get you to.
            </p>
            <p>
                Vitamin D deficiency is common enough across the general
                population that supplementing is usually simpler than
                testing to confirm it.
            </p>
            <p>
                You would need roughly a kilo of steak a day for a
                creatine dose, or oily fish several times a week for the
                omega-3 intake the study trials used.
            </p>
            <p>
                Supplementing is the practical route for both scenarios.
            </p>
            <p>
                Once you have picked something worth taking, the question
                becomes when can I judge it is working.
            </p>

            <h2>What is actually filling up</h2>
            <p>
                Creatine works by filling the creatine pool in your
                muscle. Some people notice something early, but the full
                effect is not there until that pool is full. Plasma
                creatine clears in about three hours, while the muscle
                pool takes about four weeks of daily dosing at 5 g to
                fill.
            </p>
            <p>
                That is what old school loading protocols were for.
                Taking 25 to 30 g a day for a week fills the pool faster,
                but the tradeoff was also filling the toilet. Taking 5 g
                a day and waiting achieves the same, less the mad
                diarrhea.
            </p>

            <div className="my-10 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                <figure className="flex flex-col items-center m-0">
                    <img
                        src="/blog/creatine.webp"
                        alt="TRT Plus Creatine compound card — 5 g/day dosing, muscle creatine level chart climbing to steady state over ~4 weeks, then falling after last dose."
                        loading="lazy"
                        className="w-full max-w-[380px] rounded-3xl shadow-[0_40px_100px_-20px_rgba(43,166,224,0.35)]"
                    />
                    <figcaption className="mt-4 text-sm text-white/50 italic text-center">
                        Creatine: muscle pool reaches steady state at ~4 weeks on 5 g/day.
                    </figcaption>
                </figure>
                <figure className="flex flex-col items-center m-0">
                    <img
                        src="/blog/d3-k2.webp"
                        alt="TRT Plus D3+K2 compound card — 5000 IU/day dosing, 25-OH-D storage climbing gently over ~180 days toward steady state."
                        loading="lazy"
                        className="w-full max-w-[380px] rounded-3xl shadow-[0_40px_100px_-20px_rgba(43,166,224,0.35)]"
                    />
                    <figcaption className="mt-4 text-sm text-white/50 italic text-center">
                        D3+K2: 25-OH-D storage plateaus over months, not weeks.
                    </figcaption>
                </figure>
            </div>
            <p>
                The same pattern runs through the other slow builders.
                Beta-alanine clears from plasma in about 25 minutes while
                the true measure, which is muscle carnosine, takes weeks.
                For fish oil, EPA and DHA clear in a day or two while
                membrane stores take months. Vitamin D3 clears from
                plasma in about a day while 25-OH-D, the storage form
                your lab reports, takes weeks to plateau.
            </p>
            <p>
                This is why a dose change today will not move a vitamin D
                blood test result for weeks.
            </p>

            <h2>Supplement sample timeframes</h2>
            <div className="my-8 overflow-x-auto">
                <table className="w-full text-sm text-left border-collapse">
                    <thead>
                        <tr className="border-b border-white/15">
                            <th className="py-3 pr-4 font-mono text-[11px] uppercase tracking-widest text-[#49bef5]">
                                Supplement
                            </th>
                            <th className="py-3 pr-4 font-mono text-[11px] uppercase tracking-widest text-[#49bef5]">
                                What builds
                            </th>
                            <th className="py-3 font-mono text-[11px] uppercase tracking-widest text-[#49bef5]">
                                Judge it at
                            </th>
                        </tr>
                    </thead>
                    <tbody className="text-white/80">
                        <tr className="border-b border-white/5">
                            <td className="py-3 pr-4 font-medium text-white">
                                Creatine
                            </td>
                            <td className="py-3 pr-4">Muscle creatine</td>
                            <td className="py-3">~4 weeks</td>
                        </tr>
                        <tr className="border-b border-white/5">
                            <td className="py-3 pr-4 font-medium text-white">
                                Beta-Alanine
                            </td>
                            <td className="py-3 pr-4">Muscle carnosine</td>
                            <td className="py-3">~4 weeks</td>
                        </tr>
                        <tr className="border-b border-white/5">
                            <td className="py-3 pr-4 font-medium text-white">
                                Magnesium Glycinate
                            </td>
                            <td className="py-3 pr-4">Repletion</td>
                            <td className="py-3">
                                ~6 weeks for the sleep benefit
                            </td>
                        </tr>
                        <tr className="border-b border-white/5">
                            <td className="py-3 pr-4 font-medium text-white">
                                D3 + K2
                            </td>
                            <td className="py-3 pr-4">25-OH-D storage</td>
                            <td className="py-3">~2 months</td>
                        </tr>
                        <tr>
                            <td className="py-3 pr-4 font-medium text-white">
                                Fish Oil
                            </td>
                            <td className="py-3 pr-4">Membrane stores</td>
                            <td className="py-3">~4 months</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <p>
                Fish oil is the longest of these by far. At week six it's
                still on the way up, so give it the full four months.
            </p>
            <p>
                Magnesium glycinate has a dual mechanism. There is a mild
                acute effect on sleep, attributed to the glycine, felt
                within hours. However, the fuller sleep benefit comes
                from repletion, the longer term restoration of your
                magnesium stores, and that takes about six weeks.
            </p>

            <h2>Beta-alanine, a useful case</h2>
            <p>
                Beta-alanine (the tingler) is often sold in pre-workout
                blends, which suggests it works in the session you take
                it in. It does not. Beta raises carnosine, which has to
                accumulate, and that takes around four weeks of daily
                dosing.
            </p>
            <p>
                The tingling reads like an acute effect, like it is
                working, but it is a nerve response to the dose itself
                and unrelated to muscle carnosine.
            </p>
            <p>
                It also needs to be taken daily rather than on training
                days only. Dosing on training days alone never fills the
                pool, and the full effect depends on this.
            </p>

            <figure className="my-10 flex flex-col items-center">
                <img
                    src="/blog/beta-alanine.webp"
                    alt="TRT Plus Beta-Alanine compound card — 3200 mg/day (4 caps), muscle carnosine climbing to steady state over ~4 weeks, then falling after last dose."
                    loading="lazy"
                    className="w-full max-w-[380px] rounded-3xl shadow-[0_40px_100px_-20px_rgba(43,166,224,0.35)]"
                />
                <figcaption className="mt-4 text-sm text-white/50 italic text-center">
                    Beta-alanine: muscle carnosine builds over ~4 weeks — the tingle is not the effect.
                </figcaption>
            </figure>

            <h2>NAC, where timing matters instead</h2>
            <p>
                NAC is a substrate used to create glutathione, the body's
                master antioxidant. It is consumed as glutathione is
                made, so daily dosing is a top-up rather than an
                accumulation.
            </p>
            <p>
                Its effects are acute, so it has to be timed accordingly.
                If you are taking it around a drinking session, it works
                best taken before the bender, since it needs to be
                present while the liver is detoxing as you go.
            </p>

            <figure className="my-10 flex flex-col items-center">
                <img
                    src="/blog/nac.webp"
                    alt="TRT Plus NAC compound card — 900 mg/day dosing, plasma level showing sharp daily peaks and troughs with no accumulation floor, steady state reached within the first day."
                    loading="lazy"
                    className="w-full max-w-[380px] rounded-3xl shadow-[0_40px_100px_-20px_rgba(43,166,224,0.35)]"
                />
                <figcaption className="mt-4 text-sm text-white/50 italic text-center">
                    NAC: plasma spikes and clears daily, no pool to fill — full effect in a day, so timing is the variable.
                </figcaption>
            </figure>

            <h2>Seeing it on the chart</h2>
            <p>
                Your Levels chart plots the accumulation curve, each dose
                stacking on what has not yet cleared, peaks climbing
                until intake matches elimination. Two markers sit on that
                curve: steady state, where accumulation is done and the
                curve flattens, and 97% eliminated, for when something is
                substantially gone after your last dose. TRT Plus
                calculates both of these using published studies.
            </p>
            <p>
                For supplements that accumulate in tissue, the curve is
                drawn against that pool rather than a blood level, and
                the chart marks the date each milestone lands on the
                timeline. Muscle saturation for creatine, muscle
                carnosine for beta-alanine, membrane stores for fish oil,
                25-OH-D storage for vitamin D.
            </p>

            <h2>Taking the right stack for the job</h2>
            <p>
                Three curated stacks ship in the app, each added to a
                protocol in one tap at doses pulled from published
                research in the context of hormone optimization.
            </p>
            <ul>
                <li>
                    <strong>S-Tier Supps.</strong> The non-negotiables
                    for TRT. Four supplements, the first being D3 + K2.
                </li>
                <li>
                    <strong>Liver Support.</strong> Detox and hormone
                    clearance. Two supplements, the first being NAC.
                </li>
                <li>
                    <strong>Metabolic Support.</strong> Glucose control
                    and lipid balance. Two supplements, the first being
                    fish oil.
                </li>
            </ul>
            <p>
                Pick the right supps for the job, preconfigured in the
                app, then give them the time each needs according to the
                TRT Plus Levels chart.
            </p>

            <div className="hairline my-10" />

            <p className="text-white/50 text-sm italic">
                This article is for informational purposes only and does
                not constitute medical advice. Consult with your
                healthcare provider before making medical decisions.
            </p>

            <h3>See it in the app</h3>
            <p>
                TRT Plus plots every supplement against the tissue pool
                that carries its effect, so you know exactly when each
                one is fair to judge.{" "}
                <Link to="/engine">See the engine</Link> ·{" "}
                <Link to="/">Get TRT Plus</Link>.
            </p>

            <h3>Related reading</h3>
            <ul>
                <li>
                    <Link to="/blog/understanding-dose-response">
                        Understanding Your Testosterone Dose Response
                    </Link>
                </li>
                <li>
                    <Link to="/blog/how-trt-plus-analyzes-your-bloodwork">
                        How TRT Plus Analyzes Your Bloodwork: A
                        Deterministic Engine, Then AI
                    </Link>
                </li>
            </ul>
        </>
    );
}
